// @flow

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Layer, Feature } from 'react-mapbox-gl';
import { connect } from 'react-redux';
import { Map, Set } from 'immutable';
import { featureCollection } from '@turf/helpers';
import bbox from '@turf/bbox';
import {
  Section,
  selectListingSectionsFilter,
  selectFavoriteListingSectionsFilter,
  selectSelectedListingSectionFilter,
  selectSelectedSection,
  selectFavoriteSections,
  selectShouldShowRows,
  selectOnListingsHover,
  selectOriginalListingsBySection,
} from '../modules/map';

import type { Listing } from '../types';

type Props = {
  listingFilter: Array<any>,
  favoriteListingFilter: Array<any>,
  selectedListingFilter: Array<any>,
  selected: Map<string, string> | null,
  favorites: Set<Map<string, string>>,
  showingSections: boolean,
  // eslint-disable-next-line react/no-unused-prop-types
  onListingsHover: (listings: Array<Listing>) => void,
  // eslint-disable-next-line react/no-unused-prop-types
  listings: Map<Section, Set<Listing>>,
};

type State = {
  hoveredFeatures: Array<any>,
};

const sectionFilter = ['all',
  ['match', ['geometry-type'], ['LineString', 'Point', 'Polygon'], true, false],
  ['==', ['get', 'type'], 'section'],
];

const stroke = color => ({
  'line-color': color,
  'line-width': [
    'interpolate', ['exponential', 1], ['zoom'],
    3, 0.5,
    7, 4,
  ],
});

const fill = color => ({
  'fill-color': color,
  'fill-antialias': true,
  'fill-outline-color': 'rgb(158,158,158)',
});

const layout = {
  stroke: {
    'line-join': 'round',
  },
};

const paint = {
  stroke: stroke('#b7b6b8'),
  listingStroke: stroke('#000'),
  fillListingNormal: fill('#a5cc77'),
  fillListingFavorite: fill('#e65889'),
  fillListingSelected: fill('#f6805b'),
  fillListingHover: {
    'fill-color': ['match', ['get', 'category'],
      'favorite', '#a31545',
      'selected', '#b23d18',
      '#618834', // normal
    ],
  },
};

class SectionStyles extends PureComponent<Props, State> {
  static contextTypes = {
    map: PropTypes.object.isRequired,
  };

  state = {
    hoveredFeatures: [],
  };

  componentWillMount() {
    this.context.map.on('mousemove', 'section-listing-fill', this.hoverFeature);
    this.context.map.on('mouseleave', 'section-listing-fill', this.hoverFeature);
    this.context.map.on('click', 'section-listing-fill', this.clickFeature);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.showingSections) {
      this.dispatchListingsHover(this.state.hoveredFeatures[0], nextProps);
    }
  }

  componentWillUnmount() {
    this.context.map.off('mousemove', 'section-listing-fill', this.hoverFeature);
    this.context.map.off('mouseleave', 'section-listing-fill', this.hoverFeature);
    this.context.map.off('click', 'section-listing-fill', this.clickFeature);
  }

  get hoveredFeatures() {
    const category = this.hoveredListingType;
    const properties = { category };
    return this.state.hoveredFeatures.map(feature => (
      <Feature
        key={feature.id}
        coordinates={feature.geometry.coordinates}
        properties={properties}
      />
    ));
  }

  get hoveredListingType() {
    const [feature] = this.state.hoveredFeatures;

    if (feature) {
      const listing = Section(feature);
      // $FlowFixMe
      if (listing.equals(this.props.selected)) return 'selected';
      if (this.props.favorites.has(listing)) return 'favorite';
    }

    return 'normal';
  }

  getFeatureSet = ({ properties: { section } }) => this.context.map
    .queryRenderedFeatures({
      layers: ['section-listing-fill'],
      filter: ['all',
        ['==', ['get', 'type'], 'section'],
        ['==', ['get', 'section'], section],
      ],
    })

  dispatchListingsHover(feature, { showingSections, onListingsHover, listings } = this.props) {
    if (showingSections) {
      const set = feature && listings.get(Section(feature));
      onListingsHover(set ? set.toArray() : []);
    }
  }

  hoverFeature = ({ features }) => {
    if (features) {
      const [feature] = features;

      if (!this.state.hoveredFeatures.find(({ id }) => feature.id === id)) {
        this.setState({ hoveredFeatures: this.getFeatureSet(feature) });
        this.dispatchListingsHover(feature);
      }
    } else {
      this.dispatchListingsHover();
      this.setState({ hoveredFeatures: [] });
    }
  }

  clickFeature = ({ features: [feature] }) => {
    if (this.props.showingSections) {
      const bounds = bbox(featureCollection(this.getFeatureSet(feature)));

      this.context.map.fitBounds(bounds, { padding: 40 });
    }
  };

  render() {
    if (this.props.showingSections) {
      this.context.map.getCanvas().style.cursor = this.state.hoveredFeatures.length ? 'pointer' : '';
    }

    return (
      <div>
        <Layer
          id="section-stroke"
          before="paint"
          type="line"
          sourceId="venue"
          sourceLayer="data"
          filter={sectionFilter}
          layout={layout.stroke}
          paint={paint.stroke}
        />
        <Layer
          id="section-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.listingFilter}
          paint={paint.fillListingNormal}
        />
        <Layer
          id="section-favorite-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.favoriteListingFilter}
          paint={paint.fillListingFavorite}
        />
        <Layer
          id="section-selected-listing-fill"
          before="stage"
          type="fill"
          sourceId="venue"
          sourceLayer="data"
          filter={this.props.selectedListingFilter}
          paint={paint.fillListingSelected}
        />
        <Layer
          id="section-listing-hover"
          before="stage"
          type="fill"
          paint={paint.fillListingHover}
        >
          {this.hoveredFeatures}
        </Layer>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  listingFilter: selectListingSectionsFilter(state),
  favoriteListingFilter: selectFavoriteListingSectionsFilter(state),
  selectedListingFilter: selectSelectedListingSectionFilter(state),
  favorites: selectFavoriteSections(state),
  selected: selectSelectedSection(state),
  showingSections: !selectShouldShowRows(state),
  onListingsHover: selectOnListingsHover(state),
  listings: selectOriginalListingsBySection(state),
});

export default connect(mapStateToProps, null)(SectionStyles);



// WEBPACK FOOTER //
// ./src/styles/sections.js